import React, { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import { X as XIcon } from "lucide-react";
import IconButton from "components/crm/ui/IconButton";
import useText from "hooks/useText";

// Side panel (560 wide): title, description, close; fixed footer.
// `actions`: buttons next to the cross ("Detailed view" for instance).
// `dismissible` false: Escape and a click beside it no longer close it, the
// cross and the form's own buttons only (a form already filled).
const Drawer = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 560,
  actions,
  above,
  dismissible = true,
}) => {
  const { text } = useText("crmUi");
  const titleId = useId();
  useEffect(() => {
    if (!open || !dismissible) return undefined;
    // An Escape already handled (a dropdown closing) leaves the panel open.
    const onKeyDown = (e) =>
      e.key === "Escape" && !e.defaultPrevented && onClose?.();
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, dismissible]);
  if (!open) return null;
  // Portalled out of <CrmScope>: the CRM font and text colour are set again here.
  return createPortal(
    <div className="fixed inset-0 z-40 flex justify-end font-crm text-body antialiased">
      <div
        data-testid="drawer-overlay"
        className="overlay-in absolute inset-0 bg-ink/40"
        onClick={dismissible ? onClose : undefined}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="drawer-in relative flex h-full flex-col bg-white shadow-pop"
        style={{ width }}
      >
        <header className="flex items-start gap-4 px-6 pb-6 pt-6">
          <div className="min-w-0 flex-1">
            <h2
              id={titleId}
              className="font-title text-2xl font-semibold leading-[30px] tracking-[-0.6px] text-ink"
            >
              {title}
            </h2>
            {description && (
              <div className="mt-1 text-sm text-muted">{description}</div>
            )}
          </div>
          {actions}
          <IconButton
            bordered
            icon={XIcon}
            label={text("close")}
            onClick={onClose}
          />
        </header>
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 pb-6">
          {children}
        </div>
        {/* What stays visible above the footer, outside the scroll (the comment area). */}
        {above && <div className="border-t border-line px-6 py-4">{above}</div>}
        {footer && (
          <footer className="flex items-center gap-3 border-t border-line px-6 py-4">
            {footer}
          </footer>
        )}
      </aside>
    </div>,
    document.body
  );
};

export default Drawer;
